import { useEffect, useState, useCallback, useRef } from "react";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { ServiceCard } from "@/components/ServiceCard";
import { ServiceFormDialog } from "@/components/ServiceFormDialog";
import { ConsuntivoDialog } from "@/components/ConsuntivoDialog";
import { IscrittiDialog } from "@/components/IscrittiDialog";
import { SignupDatesDialog } from "@/components/SignupDatesDialog";
import { CalendarView } from "@/components/CalendarView";
import { NotificationsBell } from "@/components/NotificationsBell";
import { BrandLogo } from "@/components/BrandLogo";
import { WhatsAppDayButton } from "@/components/WhatsAppShare";
import { VehicleWeekView } from "@/components/VehicleWeekView";
import { pushSupported, registerServiceWorker, subscribePush } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, LogOut, Moon, Sun, Inbox, Settings, FileText, CalendarDays, List, CheckSquare, Archive, Trash2, User, HelpCircle, BellRing, Radio, ClipboardCheck, Truck } from "lucide-react";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const isCoord = user.role === "coordinatore";
  const [services, setServices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("tutti");
  const [dateFilter, setDateFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const pendingRef = useRef({});
  const [view, setView] = useState("lista");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copying, setCopying] = useState(null);
  const [consuntivo, setConsuntivo] = useState(null);
  const [iscritti, setIscritti] = useState(null);
  const [signupCtx, setSignupCtx] = useState(null);
  const navigate = useNavigate();
  const { settings } = useSettings();

  const loadServices = useCallback(async () => {
    try {
      const { data } = await api.get("/services");
      setServices(data);
    } catch (e) {}
    setLoading(false);
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadServices();
    loadNotifications();
  }, [loadServices, loadNotifications]);

  useAutoRefresh(() => { loadServices(); loadNotifications(); }, 20000);

  const [pushPerm, setPushPerm] = useState(() => (typeof Notification !== "undefined" ? Notification.permission : "unsupported"));

  useEffect(() => {
    if (!pushSupported()) return;
    registerServiceWorker().then(() => {
      if (Notification.permission === "granted") subscribePush().catch(() => {});
    });
  }, []);

  const enablePush = async () => {
    const res = await subscribePush();
    if (res.ok) {
      setPushPerm("granted");
      toast.success("Avvisi attivati", { description: "Riceverai le notifiche anche con l'app chiusa." });
    } else if (res.reason === "denied") {
      setPushPerm("denied");
      toast.error("Permesso negato", { description: "Abilita le notifiche dalle impostazioni del browser." });
    } else {
      toast.error("Impossibile attivare gli avvisi");
    }
  };

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };

  const handleSignup = (svc, slot, vehicleId) => {
    const d = svc.dates || [];
    if (d.length > 1) { setSignupCtx({ svc, slot, vehicleId }); return; }
    doSignup(svc, slot, d, vehicleId);
  };

  const doSignup = async (svc, slot, dates, vehicleId) => {
    try {
      const { data } = await api.post(`/services/${svc.id}/signup`, { slot, dates, vehicle_id: vehicleId });
      setServices((prev) => prev.map((s) => (s.id === data.id ? data : s)));
      if (data.status === "coperto") {
        toast.success("Servizio coperto!", { description: `${data.event_name} ha raggiunto la copertura completa.` });
      } else {
        const label = slot === "mezzo" ? "autista del mezzo" : slot;
        toast.success("Iscrizione registrata", { description: `Ti sei segnato come ${label}.` });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleWithdraw = async (svc) => {
    try {
      const { data } = await api.post(`/services/${svc.id}/withdraw`);
      setServices((prev) => prev.map((s) => (s.id === data.id ? data : s)));
      toast("Ti sei ritirato dal servizio");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteWithUndo = (svc) => {
    setServices((prev) => prev.filter((s) => s.id !== svc.id));
    const timer = setTimeout(async () => {
      try { await api.delete(`/services/${svc.id}`); } catch (e) {}
      delete pendingRef.current[svc.id];
    }, 6000);
    pendingRef.current[svc.id] = timer;
    toast("Servizio eliminato", {
      description: svc.event_name,
      duration: 6000,
      action: {
        label: "Annulla",
        onClick: () => {
          clearTimeout(timer);
          delete pendingRef.current[svc.id];
          setServices((prev) => [...prev, svc]);
        },
      },
    });
  };

  const handleArchive = async (svc, archived) => {
    try {
      const { data } = await api.post(`/services/${svc.id}/archive`, { archived });
      setServices((prev) => prev.map((s) => (s.id === data.id ? data : s)));
      toast.success(archived ? "Servizio archiviato" : "Servizio ripristinato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const bulkDelete = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const removed = services.filter((s) => selected.has(s.id));
    setServices((prev) => prev.filter((s) => !selected.has(s.id)));
    setSelected(new Set());
    setSelectMode(false);
    const timer = setTimeout(async () => {
      try { await api.post("/services/bulk-delete", { ids }); } catch (e) {}
    }, 6000);
    toast(`${ids.length} servizi eliminati`, {
      duration: 6000,
      action: { label: "Annulla", onClick: () => { clearTimeout(timer); setServices((prev) => [...prev, ...removed]); } },
    });
  };

  const bulkArchive = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    await Promise.all(ids.map((id) => api.post(`/services/${id}/archive`, { archived: true }).catch(() => {})));
    try { const { data } = await api.get("/services"); setServices(data); } catch (e) {}
    setSelected(new Set());
    setSelectMode(false);
    toast.success(`${ids.length} servizi archiviati`);
  };

  const handleSaved = (svc, isNew) => {
    setServices((prev) => (isNew ? [...prev, svc] : prev.map((s) => (s.id === svc.id ? svc : s))));
  };

  const handleConsuntivoSaved = (svc) => {
    setServices((prev) => prev.map((s) => (s.id === svc.id ? svc : s)));
  };

  const filtered = services.filter((s) => {
    const history = s.archived;
    if (dateFilter) {
      const ds = s.dates && s.dates.length ? s.dates : [s.date_time];
      if (!ds.some((d) => (d || "").slice(0, 10) === dateFilter)) return false;
    }
    if (filter === "storico") return history;
    if (history) return false;
    if (filter === "miei") return s.is_signed_up;
    if (filter === "aperti") return s.status === "aperto";
    if (filter === "coperti") return s.status === "coperto";
    if (filter === "gestione") return isCoord;
    return true;
  });

  const stats = {
    totale: services.length,
    aperti: services.filter((s) => s.status === "aperto").length,
    coperti: services.filter((s) => s.status === "coperto").length,
    miei: services.filter((s) => s.is_signed_up).length,
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="cri-topbar" />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo logoUrl={settings.logo_url} size={43} />
            <div className="leading-tight">
              <p className="font-display font-bold text-sm sm:text-base">Gestione Servizi CRI</p>
              <p className="text-xs text-muted-foreground hidden sm:block">{settings.committee_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {isCoord && (
              <Button variant="ghost" size="sm" data-testid="report-link" onClick={() => navigate("/report")} className="rounded-full hidden sm:inline-flex">
                <FileText className="h-4 w-4 mr-1" /> Report
              </Button>
            )}
            {isCoord && (
              <Button variant="ghost" size="sm" data-testid="radio-link" onClick={() => navigate("/radio")} className="rounded-full hidden sm:inline-flex">
                <Radio className="h-4 w-4 mr-1" /> Radio
              </Button>
            )}
            {isCoord && (
              <Button variant="ghost" size="sm" data-testid="checklist-link" onClick={() => navigate("/checklist")} className="rounded-full hidden sm:inline-flex">
                <ClipboardCheck className="h-4 w-4 mr-1" /> Check-list
              </Button>
            )}
            {isCoord && (
              <Button variant="ghost" size="sm" data-testid="volunteers-link" onClick={() => navigate("/volontari")} className="rounded-full hidden sm:inline-flex">
                <User className="h-4 w-4 mr-1" /> Volontari
              </Button>
            )}
            {user.is_admin && (
              <Button variant="ghost" size="sm" data-testid="admin-link" onClick={() => navigate("/admin")} className="rounded-full hidden sm:inline-flex">
                <Settings className="h-4 w-4 mr-1" /> Admin
              </Button>
            )}
            <Button variant="ghost" size="icon" data-testid="theme-toggle" onClick={toggleDark} className="rounded-full">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            {pushSupported() && pushPerm !== "granted" && (
              <Button variant="outline" size="sm" data-testid="enable-push" onClick={enablePush} className="rounded-full font-semibold" title="Attiva avvisi push">
                <BellRing className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Attiva avvisi</span>
              </Button>
            )}
            <NotificationsBell notifications={notifications} onRefresh={loadNotifications} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-muted transition-colors">
                  <span className="h-8 w-8 rounded-full bg-[#D32F2F] text-white flex items-center justify-center text-sm font-bold">
                    {user.name?.[0]?.toUpperCase()}
                  </span>
                  <span className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-sm font-semibold">{user.name}</span>
                    <span className="text-[11px] text-muted-foreground capitalize">{user.role}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="profile-link" onClick={() => navigate("/profilo")}>
                  <User className="h-4 w-4 mr-2" /> Il mio profilo
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="guide-link" onClick={() => window.open("/guida-volontari.pdf", "_blank")}>
                  <HelpCircle className="h-4 w-4 mr-2" /> Manuale operativo
                </DropdownMenuItem>
                {isCoord && (
                  <DropdownMenuItem data-testid="report-link-mobile" onClick={() => navigate("/report")}>
                    <FileText className="h-4 w-4 mr-2" /> Report ore
                  </DropdownMenuItem>
                )}
                {isCoord && (
                  <DropdownMenuItem data-testid="radio-link-mobile" onClick={() => navigate("/radio")}>
                    <Radio className="h-4 w-4 mr-2" /> Gestione radio
                  </DropdownMenuItem>
                )}
                {isCoord && (
                  <DropdownMenuItem data-testid="checklist-link-mobile" onClick={() => navigate("/checklist")}>
                    <ClipboardCheck className="h-4 w-4 mr-2" /> Check-list
                  </DropdownMenuItem>
                )}
                {isCoord && (
                  <DropdownMenuItem data-testid="volunteers-link-mobile" onClick={() => navigate("/volontari")}>
                    <User className="h-4 w-4 mr-2" /> Volontari e qualifiche
                  </DropdownMenuItem>
                )}
                {user.is_admin && (
                  <DropdownMenuItem data-testid="admin-link-mobile" onClick={() => navigate("/admin")}>
                    <Settings className="h-4 w-4 mr-2" /> Amministrazione
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero / title */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            {settings.show_hero && (
              <>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
                  {isCoord ? "Coordinamento servizi" : "Servizi disponibili"}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {isCoord ? "Pubblica richieste e monitora la copertura in tempo reale." : "Candidati ai turni aperti del comitato."}
                </p>
              </>
            )}
          </div>
          {isCoord && (
            <div className="flex items-center gap-2">
              <Button variant="outline" data-testid="select-mode-toggle" onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} className="rounded-full font-semibold">
                <CheckSquare className="h-4 w-4 mr-1" /> {selectMode ? "Annulla selezione" : "Seleziona"}
              </Button>
              <Button data-testid="create-service-button" onClick={() => setCreateOpen(true)} className="rounded-full font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Nuovo servizio
              </Button>
            </div>
          )}
        </div>

        {/* Stats */}
        {settings.show_stats && (
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { k: "totale", l: "Servizi totali", v: stats.totale },
            { k: "aperti", l: "Da coprire", v: stats.aperti },
            { k: "coperti", l: "Coperti", v: stats.coperti },
            { k: "miei", l: "I miei turni", v: stats.miei },
          ].map((s, i) => (
            <motion.div
              key={s.k}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.05 }}
              data-testid={`stat-${s.k}`}
              className="rounded-xl border border-border bg-card p-4"
            >
              <p className="text-3xl font-display font-extrabold">{s.v}</p>
              <p className="text-sm text-muted-foreground">{s.l}</p>
            </motion.div>
          ))}
        </div>
        )}

        {/* Filters + view toggle */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {settings.show_filters ? (
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList data-testid="service-filters" className="flex-wrap h-auto">
                  <TabsTrigger value="tutti" data-testid="filter-tutti">Tutti</TabsTrigger>
                  <TabsTrigger value="aperti" data-testid="filter-aperti">Da coprire</TabsTrigger>
                  <TabsTrigger value="coperti" data-testid="filter-coperti">Coperti</TabsTrigger>
                  <TabsTrigger value="miei" data-testid="filter-miei">I miei turni</TabsTrigger>
                  {isCoord && <TabsTrigger value="gestione" data-testid="filter-gestione">Da gestire</TabsTrigger>}
                  <TabsTrigger value="storico" data-testid="filter-storico">Storico</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" data-testid="date-filter" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="h-9 w-auto" />
              {dateFilter && (
                <Button variant="ghost" size="sm" data-testid="date-filter-clear" onClick={() => setDateFilter("")} className="rounded-full">
                  Azzera data
                </Button>
              )}
              {isCoord && <WhatsAppDayButton date={dateFilter} />}
              {isCoord && <WhatsAppDayButton date={dateFilter} week />}
            </div>
          </div>
          <Tabs value={view} onValueChange={setView}>
            <TabsList data-testid="view-toggle">
              <TabsTrigger value="lista" data-testid="view-lista"><List className="h-4 w-4 mr-1" /> Lista</TabsTrigger>
              <TabsTrigger value="calendario" data-testid="view-calendario"><CalendarDays className="h-4 w-4 mr-1" /> Calendario</TabsTrigger>
              {isCoord && <TabsTrigger value="mezzi" data-testid="view-mezzi"><Truck className="h-4 w-4 mr-1" /> Mezzi</TabsTrigger>}
            </TabsList>
          </Tabs>
        </div>

        {/* Content */}
        {selectMode && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid="bulk-bar">
            <span className="text-sm font-medium">{selected.size} selezionati</span>
            <Button size="sm" variant="outline" data-testid="bulk-archive" disabled={selected.size === 0} onClick={bulkArchive} className="rounded-full">
              <Archive className="h-4 w-4 mr-1" /> Archivia
            </Button>
            <Button size="sm" variant="destructive" data-testid="bulk-delete" disabled={selected.size === 0} onClick={bulkDelete} className="rounded-full">
              <Trash2 className="h-4 w-4 mr-1" /> Elimina
            </Button>
          </div>
        )}
        {loading ? (
          <p className="mt-10 text-center text-muted-foreground">Caricamento…</p>
        ) : view === "calendario" ? (
          <div className="mt-6">
            <CalendarView services={services} onSelect={(s) => (isCoord ? setEditing(s) : null)} />
          </div>
        ) : view === "mezzi" && isCoord ? (
          <div className="mt-6">
            <VehicleWeekView services={services} onSelect={(s) => setEditing(s)} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 flex flex-col items-center text-center">
            <Inbox className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-3 font-display text-lg font-semibold">Nessun servizio</p>
            <p className="text-muted-foreground text-sm">
              {isCoord ? "Pubblica il primo servizio con «Nuovo servizio»." : "Al momento non ci sono turni in questa vista."}
            </p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((svc) => (
              <ServiceCard
                key={svc.id}
                svc={svc}
                currentUser={user}
                onSignup={handleSignup}
                onWithdraw={handleWithdraw}
                onDelete={deleteWithUndo}
                onEdit={setEditing}
                onDuplicate={setCopying}
                onConsuntivo={setConsuntivo}
                onIscritti={setIscritti}
                onArchive={handleArchive}
                onRefresh={loadServices}
                selectMode={selectMode}
                selected={selected.has(svc.id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </div>
        )}
      </main>

      <ServiceFormDialog open={createOpen} onOpenChange={setCreateOpen} service={null} onSaved={handleSaved} />
      <ServiceFormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} service={editing} onSaved={handleSaved} />
      <ServiceFormDialog open={!!copying} onOpenChange={(o) => !o && setCopying(null)} service={null} template={copying} onSaved={handleSaved} />
      <ConsuntivoDialog open={!!consuntivo} onOpenChange={(o) => !o && setConsuntivo(null)} service={consuntivo} onSaved={handleConsuntivoSaved} />
      <IscrittiDialog open={!!iscritti} onOpenChange={(o) => !o && setIscritti(null)} service={iscritti} onSaved={handleConsuntivoSaved} />
      <SignupDatesDialog open={!!signupCtx} onOpenChange={(o) => !o && setSignupCtx(null)} service={signupCtx?.svc} slot={signupCtx?.slot} onConfirm={(dates) => { doSignup(signupCtx.svc, signupCtx.slot, dates, signupCtx.vehicleId); setSignupCtx(null); }} />
    </div>
  );
}
